import styled from '@emotion/styled'
import { Alert, App, Button } from '@vivaldis/antd-ui'
import { doesEmployeeHaveExpiredContractReadyInformation } from '@vivaldis/common'
import { EmployeeStatesEnum } from '@vivaldis/graphql'
import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { EmployeeProfileQuery_employee } from '../../typings/EmployeeProfileQuery'

interface Props {
  employee: EmployeeProfileQuery_employee
  onSendInvitationReminder?: () => Promise<void>
  onSendMissingContractInformationPushNotification: () => Promise<void>
}

export const EmployeeStatusAlert = ({
  employee,
  onSendInvitationReminder,
  onSendMissingContractInformationPushNotification
}: Props) => {
  const [t] = useTranslation('web_ui')
  const { modal } = App.useApp()

  const [isRequestSent, setIsRequestSent] = useState(false)

  const [isSendingRequest, setIsSendingRequest] = useState(false)

  const employeeName = useMemo(
    () => `${employee.firstName} ${employee.lastName}`,
    [employee]
  )

  const handleSendInvitationReminder = useCallback(async () => {
    setIsSendingRequest(true)
    try {
      await onSendInvitationReminder?.()
      setIsRequestSent(true)
    } catch {}
    setIsSendingRequest(false)
  }, [onSendInvitationReminder])

  const handleSendMissingContractInformationPushNotification =
    useCallback(async () => {
      setIsSendingRequest(true)

      try {
        await onSendMissingContractInformationPushNotification()
        setIsRequestSent(true)
      } catch {}

      setIsSendingRequest(false)
    }, [onSendMissingContractInformationPushNotification])

  const handleReSendInviteButtonClick = useCallback(() => {
    modal.confirm({
      title: t(
        'employee_profile.employee_status_alert.resend_invitation_confirmation_dialog.title'
      ),
      content: t(
        'employee_profile.employee_status_alert.resend_invitation_confirmation_dialog.content'
      ),
      onOk: async () => {
        await handleSendInvitationReminder()
      }
    })
  }, [handleSendInvitationReminder, modal, t])

  if (isRequestSent) {
    return null
  }

  if (employee.state === EmployeeStatesEnum.AWAITING_REGISTRATION) {
    if (onSendInvitationReminder) {
      return (
        <Alert
          description={
            <div>
              <Trans
                t={t}
                i18nKey="employee_profile.employee_status_alert.alerts.awaiting_registration.body"
              >
                <div>
                  We've emailed {{ employeeName } as any} instructions to
                  complete the contract details.
                </div>
                <div>
                  You can also <span>update the profile</span> yourself or
                  <SmallLinkButton
                    type="link"
                    size="small"
                    onClick={handleReSendInviteButtonClick}
                    loading={isSendingRequest}
                  >
                    send a reminder
                  </SmallLinkButton>
                  .
                </div>
              </Trans>
            </div>
          }
          type="warning"
          closable
          style={{ marginBottom: 24 }}
        />
      )
    }
    return (
      <Alert
        description={t(
          'employee_profile.employee_status_alert.alerts.awaiting_registration_without_send_reminded_button.body',
          {
            employeeName: employeeName
          }
        )}
        type="warning"
        closable
        style={{ marginBottom: 24 }}
      />
    )
  }

  if (employee.state === EmployeeStatesEnum.UNVERIFIED) {
    const employeeHasExpiredInformation =
      doesEmployeeHaveExpiredContractReadyInformation(employee)
    if (employeeHasExpiredInformation) {
      return (
        <Alert
          description={
            <div>
              <Trans
                t={t}
                i18nKey="employee_profile.employee_status_alert.alerts.expired_information.body"
              >
                <div>
                  We've notified {{ employeeName } as any} with instructions to
                  update the expired contract details.
                </div>
                <div>
                  Alternatively, you can
                  {<span> update the employee profile </span>}
                  with the updated information or
                  <SmallLinkButton
                    type="link"
                    size="small"
                    onClick={
                      handleSendMissingContractInformationPushNotification
                    }
                    loading={isSendingRequest}
                  >
                    send a reminder notification
                  </SmallLinkButton>
                  .
                </div>
              </Trans>
            </div>
          }
          type="warning"
          closable
          style={{ marginBottom: 24 }}
        />
      )
    }

    return (
      <Alert
        description={
          <div>
            <Trans
              t={t}
              i18nKey="employee_profile.employee_status_alert.alerts.missing_information.body"
            >
              <div>
                We're missing required contract information of{' '}
                {{ employeeName } as any}.
              </div>
              <div>
                Please
                {<span> update the employee profile </span>}
                with the required information or
                <SmallLinkButton
                  type="link"
                  size="small"
                  onClick={handleSendMissingContractInformationPushNotification}
                  loading={isSendingRequest}
                >
                  send a reminder notification.
                </SmallLinkButton>
                .
              </div>
            </Trans>
          </div>
        }
        type="warning"
        closable
        style={{ marginBottom: 24 }}
      />
    )
  }

  return null
}

const SmallLinkButton = styled(Button)`
  display: inline;
  white-space: initial;
  padding: 0 !important;
  > span {
    text-decoration: underline;
    display: inline !important;
  }
`
